import {ReactNode} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {Ban, Blocks, ChartColumn, ChevronLeft, LifeBuoy, ListTree, LogOut, Phone, PhoneCall, ShieldCheck, SlidersHorizontal, SquareArrowOutUpRight, User, UserCog, Users, UsersRound, Volume2} from 'lucide-react';
import {useSelector} from 'react-redux';
import {selectUserRole} from '../../store/features/Auth/authSlice';
import {ContentHeader, ListPanel, StatusBadge} from 'ch3-ui-lib';
import {activeSettingsItem, SettingsItem, SettingsItemId, visibleSettingsSections} from './settingsNav';
import {allAllowed, useBrowserPermission} from './browserPermissions';
import {requestLogout} from '../../components/sidebar/LogoutDialog';

const lucide = '!p-0 shrink-0 !text-current';

// Injected by Vite at build time; absent in unit tests.
const APP_VERSION = typeof __APP_VERSION__ === 'undefined' ? '' : __APP_VERSION__;

const ICONS: Record<SettingsItemId, typeof User> = {
    general: SlidersHorizontal,
    profile: User,
    phones: Phone,
    groups: Users,
    queues: ListTree,
    audio: Volume2,
    permissions: ShieldCheck,
    'call-restrictions': Ban,
    help: LifeBuoy,
    reports: ChartColumn,
    teamAdmin: UserCog,
    groupsAdmin: UsersRound,
    phonesAdmin: PhoneCall,
    integrations: Blocks,
};

const NavRow = ({item, active, trailing}: { item: SettingsItem; active: boolean; trailing?: ReactNode }) => {
    const Icon = ICONS[item.id];
    const content = (
        <>
            <span className={`flex size-10 shrink-0 items-center justify-center rounded-[12px] ${active ? 'bg-brand-tint text-brand-ink' : 'bg-surface-muted text-ink-secondary'}`}>
                <Icon size={20} aria-hidden="true" className={lucide}/>
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-body-strong text-ink">{item.label}</span>
                <span className={`truncate text-footnote ${active ? 'text-ink-muted-strong' : 'text-ink-muted'}`}>{item.description}</span>
            </span>
            {trailing}
            {item.href && <SquareArrowOutUpRight size={16} aria-hidden="true" className={`${lucide} text-ink-muted`}/>}
        </>
    );
    const className = `flex h-14 items-center gap-3 rounded-control px-2 outline-none focus-visible:ring-2 focus-visible:ring-brand-focus ${active ? 'bg-brand-tint-subtle' : 'hover:bg-surface-muted'}`;
    if (item.href) {
        return <a href={item.href} target="_blank" rel="noreferrer" className={className}>{content}</a>;
    }
    return <Link to={item.to!} aria-current={active ? 'page' : undefined} className={className}>{content}</Link>;
};

export const SettingsNav = () => {
    const {pathname} = useLocation();
    const active = activeSettingsItem(pathname);
    const microphone = useBrowserPermission('microphone');
    const notifications = useBrowserPermission('notifications');
    const permissionsKnown = microphone.state !== undefined && notifications.state !== undefined;
    const permissionsOk = allAllowed(microphone.state, notifications.state);
    const role = useSelector(selectUserRole);

    return (
        <ListPanel label="Settings" title="Settings">
            <nav aria-label="Settings sections" className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2">
                {visibleSettingsSections(role).map((section) => (
                    <div key={section.title} className="flex flex-col">
                        <div className="px-2 pb-1 pt-3 text-caption text-ink-muted">{section.title}</div>
                        {section.items.map((item) => (
                            <NavRow
                                key={item.id}
                                item={item}
                                active={item.id === active}
                                trailing={item.id === 'permissions' && permissionsKnown && (
                                    permissionsOk
                                        ? <StatusBadge tone="success" className="!h-[22px] !text-caption">All allowed</StatusBadge>
                                        : <StatusBadge tone="warning" className="!h-[22px] !text-caption">Needs attention</StatusBadge>
                                )}
                            />
                        ))}
                    </div>
                ))}
                <div className="flex-1"/>
                <div className="flex items-center gap-2 py-2">
                    <button
                        type="button"
                        // The confirmation dialog lives with the avatar menu in the sidebar.
                        onClick={requestLogout}
                        className="inline-flex h-control-md cursor-pointer items-center gap-2 rounded-control bg-surface-muted px-3 text-callout font-medium text-danger-ink hover:bg-line"
                    >
                        <LogOut size={16} aria-hidden="true" className={lucide}/>
                        Log out
                    </button>
                    {APP_VERSION && APP_VERSION !== '0.0.0' && (
                        <span className="px-1 text-footnote font-medium text-ink-muted">PhoneHQ Web {APP_VERSION}</span>
                    )}
                </div>
            </nav>
        </ListPanel>
    );
};

interface SettingsLayoutProps {
    title: ReactNode;
    /** Link back to the parent page, e.g. from one phone number to the list. */
    back?: { label: string; to: string };
    actions?: ReactNode;
    children: ReactNode;
}

// Settings frame: list panel on the left, the chosen page on canvas with a 56px header.
export const SettingsLayout = ({title, back, actions, children}: SettingsLayoutProps) => (
    <div className="flex h-[calc(100vh-var(--header-height))] min-h-0">
        <SettingsNav/>
        <main className="flex min-w-0 flex-1 flex-col bg-canvas">
            <ContentHeader className="bg-surface">
                {back && (
                    <Link to={back.to} className="inline-flex h-7 items-center gap-1 rounded-chip pl-1 pr-2 text-callout text-ink-secondary hover:bg-surface-muted">
                        <ChevronLeft size={18} aria-hidden="true" className={lucide}/>
                        {back.label}
                    </Link>
                )}
                {typeof title === 'string' ? <h1 className="m-0 flex-1 truncate text-title text-ink">{title}</h1> : <div className="min-w-0 flex-1">{title}</div>}
                {actions}
            </ContentHeader>
            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </main>
    </div>
);

interface SettingsGroupProps {
    title: string;
    description?: string;
    children: ReactNode;
}

/** One row of a settings page: a 240px description column and its cards. */
export const SettingsGroup = ({title, description, children}: SettingsGroupProps) => (
    <section aria-label={title} className="grid grid-cols-1 gap-3 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-8">
        <div>
            <h2 className="m-0 text-body-strong text-ink">{title}</h2>
            {description && <p className="m-0 mt-1 text-footnote text-ink-muted">{description}</p>}
        </div>
        <div className="flex min-w-0 flex-col gap-3">{children}</div>
    </section>
);

export const SettingsPage = ({children}: { children: ReactNode }) => (
    <div className="flex w-full max-w-[1040px] flex-col gap-6 p-8">{children}</div>
);

/** White card with 44px+ rows separated by a hairline. */
export const SettingsCard = ({children}: { children: ReactNode }) => (
    <div className="flex flex-col rounded-card bg-surface px-4 [&>*+*]:border-0 [&>*+*]:border-t [&>*+*]:border-solid [&>*+*]:border-surface-muted">
        {children}
    </div>
);
